'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, X } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { EmptyState } from '@/components/shared/EmptyState';
import { GameCard } from '@/components/shared/m-game-card';
import {
  getVisiblePages,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { sortProviders } from '@/features/game-categories/helpers';
import type { GameCategory } from '@/features/game-categories/types';
import { useGames } from '@/features/games/hooks/useGames';
import { cn } from '@/lib/utils';
import { FavoriteGame } from '../favorites';
import { HotGames } from '../hot-games';

interface CategoryContentProps {
  rawSearch?: string;
  search?: string;
  category?: GameCategory;
  isLoading?: boolean;
}

export const CategoryContent = ({
  rawSearch,
  search,
  category,
  isLoading,
}: CategoryContentProps) => {
  const t = useTranslations();

  // const SORT_OPTIONS = [
  //   { value: 'all', label: t('ui.games.sort_providers') || 'All' },
  //   { value: 'popular', label: t('ui.games.sort_popular') || 'Popular' },
  //   { value: 'new', label: t('ui.games.sort_new') || 'New' },
  // ] as const;

  const [sortValue, _setSortValue] = useState<string>('all');
  const [activeProviderId, setActiveProviderId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset page on filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeProviderId, sortValue, search]);

  const {
    data: gamesPage,
    isLoading: gamesLoading,
    isFetching: gamesFetching,
    isPlaceholderData,
  } = useGames({
    categoryId: category?.id,
    page: currentPage,
    limit: 12,
    product: activeProviderId ?? undefined,
    hot: sortValue === 'popular',
    isNew: sortValue === 'new',
    search: search || undefined,
  });

  const games = gamesPage?.games ?? [];
  const pagination = gamesPage?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  const providers = sortProviders(category?.providers ?? []);
  const selectedProvider = providers.find(p => p.id === activeProviderId);

  const goPrev = () => setCurrentPage(p => Math.max(1, p - 1));
  const goNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));

  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= totalPages;

  const pageItems = getVisiblePages(currentPage, totalPages);

  const isSearchTyping =
    Boolean(rawSearch?.trim()) && rawSearch?.trim() !== (search ?? '').trim();

  const isContentLoading =
    isLoading ||
    gamesLoading ||
    gamesFetching ||
    isPlaceholderData ||
    isSearchTyping;

  return (
    <div className="mb-4">
      {/* Sort Options & Providers Selector Row */}
      <div className="px-4 mb-3 flex items-center justify-start gap-3">
        {/* <Select value={sortValue} onValueChange={setSortValue}>
          <SelectTrigger
            className="w-[120px] h-9 border-white/10 text-white/90 hover:bg-[#801C80]/30 !bg-[#801C80]/40 !rounded-sm !text-xs"
            aria-label="Sort games"
          >
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="border-[#3d2a5c] bg-[#2d1b4e]">
            {SORT_OPTIONS.map(opt => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                className="text-[#a78bfa] focus:bg-[#3d2a5c] focus:text-white"
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select> */}

        {providers.length > 0 && (
          <button
            type="button"
            onClick={() => setIsProviderModalOpen(true)}
            className="flex h-9 items-center gap-1.5 rounded-sm border border-white/10 bg-[#801C80]/40 px-3 transition-all hover:bg-[#801C80]/60 active:scale-95 text-white/90"
          >
            {selectedProvider ? (
              selectedProvider.image ? (
                <div className="relative w-10 h-5">
                  <Image
                    src={selectedProvider.image}
                    alt={selectedProvider.name}
                    fill
                    className="object-contain"
                  />
                </div>
              ) : (
                <span className="text-[12px] font-bold text-white">
                  {selectedProvider.name}
                </span>
              )
            ) : (
              <span className="text-[12px] font-bold text-white/60">
                {t('ui.games.sort_providers') || 'Providers'}
              </span>
            )}
            <ChevronDown className="size-3.5 text-white/40" />
          </button>
        )}
      </div>

      {/* Games Grid */}
      <div className="mx-2 rounded-lg bg-[#801C80]/40 p-2">
        {isContentLoading ? (
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton loading cards
                key={`skeleton-${i}`}
                className="h-[160px] animate-pulse rounded-sm bg-white/5"
              />
            ))}
          </div>
        ) : games.length === 0 ? (
          <EmptyState
            search={search || rawSearch}
            providerName={selectedProvider?.name}
          />
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {games.map(game => (
              <GameCard
                key={game.id}
                gameId={game.id}
                imageUrl={game.image ?? undefined}
                title={game.name}
                liked={game.isFavorite}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!gamesLoading && totalPages > 1 && (
        <Pagination className="pt-4 pb-2">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious disabled={isPrevDisabled} onClick={goPrev} />
            </PaginationItem>
            {pageItems.map((item, idx) =>
              item === 'ellipsis' ? (
                <PaginationItem
                  key={`ellipsis-${pageItems[idx - 1]}-${pageItems[idx + 1]}`}
                >
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    isActive={item === currentPage}
                    onClick={() => setCurrentPage(item)}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext disabled={isNextDisabled} onClick={goNext} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <div className="flex flex-col gap-4 mt-4">
        <FavoriteGame categoryName={category?.name} />
        <HotGames categoryId={category?.id} />
      </div>

      {/* Reusable Category Providers Bottom Sheet Sheet Modal */}
      <CategoryProviderModal
        isOpen={isProviderModalOpen}
        onClose={() => setIsProviderModalOpen(false)}
        providers={providers}
        selectedProviderId={activeProviderId}
        onSelect={setActiveProviderId}
      />
    </div>
  );
};

interface CategoryProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  providers: { id: string; name: string; image: string | null }[];
  selectedProviderId: string | null;
  onSelect: (providerId: string | null) => void;
}

const CategoryProviderModal = ({
  isOpen,
  onClose,
  providers,
  selectedProviderId,
  onSelect,
}: CategoryProviderModalProps) => {
  const t = useTranslations();

  const displayProviders = [
    { id: 'all', name: 'All Providers', image: null },
    ...providers,
  ];

  const providerGrid = (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-8 pt-2 no-scrollbar">
      <div className="grid grid-cols-3 gap-2">
        {displayProviders.map(provider => {
          const isSelected =
            provider.id === 'all'
              ? selectedProviderId === null
              : selectedProviderId === provider.id;

          return (
            <div
              key={provider.id}
              className={cn(
                'transition-all p-0.5',
                isSelected
                  ? 'bg-[#FFA035] rounded-sm'
                  : 'bg-[linear-gradient(to_right,#4C1D80_0%,#5A1990_26%,#6D13A6_71%,#7411AE_100%)] rounded-sm'
              )}
            >
              <button
                type="button"
                onClick={() => {
                  onSelect(provider.id === 'all' ? null : provider.id);
                  onClose();
                }}
                className={cn(
                  'relative w-full flex items-center justify-center h-[52px] rounded-sm transition-all px-2 bg-[#1E1B4B]',
                  isSelected
                    ? 'shadow-[0_0_15px_rgba(255,211,93,0.15)]'
                    : 'border-white/5 hover:bg-white/10'
                )}
              >
                {provider.id === 'all' ? (
                  <span className="text-[11px] font-bold text-white/90">
                    All
                  </span>
                ) : provider.image ? (
                  <div className="relative w-full h-[26px]">
                    <Image
                      src={provider.image}
                      alt={provider.name}
                      fill
                      className="object-contain filter brightness-110"
                    />
                  </div>
                ) : (
                  <span className="text-[10px] font-medium text-white/80 text-center line-clamp-2 leading-tight">
                    {provider.name}
                  </span>
                )}
                {isSelected && (
                  <div className="absolute -bottom-0.5 -right-0.5 size-4 bg-[#FFA035] rounded-full flex items-center justify-center">
                    <Check className="size-3 text-[#301552] stroke-[4]" />
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm"
          />

          {/* Sheet: slide-up from bottom (mobile) */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-[121] max-h-[85vh] overflow-hidden rounded-t-[32px] border-t border-white/10 bg-[#090322] flex flex-col max-w-[500px] mx-auto w-full"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-[#D9D9D9]" />
            </div>

            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-center relative flex-shrink-0">
              <h2 className="text-[15px] font-bold text-white">
                {t('ui.casino.sort_providers')}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-white/50 hover:text-white"
              >
                <X className="size-5" />
              </button>
            </div>

            {providerGrid}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
